import { Box, Flex, Heading, Stack, StackDivider } from '@chakra-ui/layout';
import {
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
} from '@chakra-ui/react';
import { type ReactNode, useContext, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { PagePaths, PageTitles } from '@revelio/core';

import { CircleIcon } from '../../../shared/ui/icons';
import { useIsThemedAppearance } from '../../../shared/user-preferences/hooks/useIsThemedAppearance';
import { PageProvider } from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import { PageStateProvider } from '../../CustomDash/components/PageState/PageStateProvider';
import { FilterProvider } from '../../CustomDash/filters/contexts/FilterProvider';
import { FilterId } from '../../CustomDash/filters/types/filters';
import { REPORT_TEMPLATES } from '../../CustomDash/templates/report-templates-registry';
import DashboardPage from '../../DashboardPage';
import { useGlobalAssistantReportLaunchPendingStore } from '../../global-ai-assistant/global-ai-assistant-report-launch-pending';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../utils/report-builder-paths';
import {
  CustomReportBuilderContext,
  CustomReportBuilderProvider,
} from './custom-report-builder-provider';
import { useAllReports } from './report-builder.repository';
import styles from './report-builder-page.module.css';

interface IStep {
  id: string;
  title: string;
  path: ReportBuilderDeliverablePaths;
  description?: string | ReactNode;
}

export enum ReportBuilderDeliverablePaths {
  REPORT_TYPE = 'report_type',
  ENTITY = 'entity',
  CONFIRMATION = 'confirmation',
}

export const steps: IStep[] = [
  {
    id: 'report_type',
    title: 'Report Type',
    path: ReportBuilderDeliverablePaths.REPORT_TYPE,
  },
  {
    id: 'entity',
    title: 'Entity',
    path: ReportBuilderDeliverablePaths.ENTITY,
  },
  {
    id: 'confirmation',
    title: 'Confirmation',
    path: ReportBuilderDeliverablePaths.CONFIRMATION,
  },
];

type StepBoxStatus = 'complete' | 'active' | 'incomplete';

const getStepStatus = (
  index: number,
  currentStepIndex: number
): StepBoxStatus => {
  if (index < currentStepIndex) return 'complete';
  if (index === currentStepIndex) return 'active';
  return 'incomplete';
};

/**
 * Custom vertical stepper used in the themed (non-legacy) appearance. Renders
 * the Figma "numbered bordered boxes" layout directly (no Chakra `<Stepper>`),
 * so the box/connector alignment matches the design instead of fighting
 * Chakra's circular `StepIndicator`.
 *
 * v1.1: steps stacked with a 16px gap and a 1px vertical connector running from
 * the bottom-centre of one box to the top-centre of the next. Monochrome:
 * active = full-ink border + ink number; complete = inverse fill; incomplete =
 * faint ink fill + secondary number.
 */
const ThemedStepper = ({ currentStepIndex }: { currentStepIndex: number }) => (
  <div className={styles.themedStepper}>
    {steps.map((step, index) => {
      const status = getStepStatus(index, currentStepIndex);
      return (
        <div className={styles.themedStep} key={`dlStep-${index}`}>
          <div className={styles.themedStepIndicator}>
            <div
              className={styles.themedStepBox}
              data-status={status}
              data-testid={
                status === 'active'
                  ? `deliverable_${step.id}_step_active_icon`
                  : undefined
              }
            >
              {index + 1}
            </div>
            {index < steps.length - 1 && (
              <span className={styles.themedStepConnector} aria-hidden />
            )}
          </div>
          <div className={styles.themedStepText}>
            <span className={styles.themedStepTitle}>{step.title}</span>
            {step.description && (
              <span className={styles.themedStepDescription}>
                {step.description}
              </span>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * Original Chakra `<Stepper>`, rendered verbatim for the legacy (un-themed)
 * appearance. Kept byte-identical to the pre-theming markup so legacy users see
 * exactly the circle/check indicator they always have.
 */
const LegacyStepper = ({ currentStepIndex }: { currentStepIndex: number }) => (
  <Stepper
    colorScheme="green"
    index={currentStepIndex}
    orientation="vertical"
    height="500px"
    maxH="500px"
    gap="0"
    size="md"
  >
    {steps.map((step, index) => (
      <Step key={`dlStep-${index}`}>
        <StepIndicator>
          <StepStatus
            complete={<StepIcon />}
            incomplete={<CircleIcon boxSize="12" color="gray.200" />}
            active={
              <CircleIcon
                boxSize="7"
                color="green.500"
                data-testid={`deliverable_${step.id}_step_active_icon`}
              />
            }
          />
        </StepIndicator>

        <Box>
          <StepTitle as="span">
            <Heading
              as="h1"
              pt="6px"
              pl="6px"
              color="text.primary"
              fontWeight="600"
              fontSize="md"
            >
              {step.title}
            </Heading>
          </StepTitle>
          {step.description && typeof step.description === 'string' ? (
            <Box pl="6px">
              <StepDescription>{step.description}</StepDescription>
            </Box>
          ) : (
            <Text as="span" fontSize="2xs" color="text.primary">
              {step.description}
            </Text>
          )}
        </Box>

        <StepSeparator />
      </Step>
    ))}
  </Stepper>
);

export const ReportBuilderPage = () => {
  return (
    <CustomReportBuilderProvider>
      <ReportBuilderPageContent />
    </CustomReportBuilderProvider>
  );
};

const ReportBuilderPageContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedReportTemplate } = useContext(CustomReportBuilderContext);
  const reportBuilderDeliverables = useAllReports();
  const pendingPromotedTemplateId = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.promotedTemplateId
  );

  const isThemedAppearance = useIsThemedAppearance();

  const currentStepIndex = steps.findIndex((step) =>
    location.pathname.includes(step.path)
  );

  useEffect(() => {
    if (location.search.length > 0) {
      navigate(
        {
          pathname: location.pathname,
          search: '',
        },
        { replace: true }
      );
      return;
    }

    if (location.pathname.endsWith(PagePaths.REPORT_BUILDER)) {
      navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE), {
        replace: true,
      });
      return;
    }

    const deliverablePath = location.pathname.split('/').pop() as string;
    if (
      Object.values(ReportBuilderDeliverablePaths).includes(
        deliverablePath as ReportBuilderDeliverablePaths
      )
    ) {
      const currentStepIndex = steps.findIndex(
        (s) => s.path === deliverablePath
      );

      const requiresReportContext =
        currentStepIndex > ReportBuilderStep.REPORT_TYPE;
      const hasReportContext =
        selectedReportTemplate !== null ||
        reportBuilderDeliverables.length > 0 ||
        pendingPromotedTemplateId !== null;

      if (requiresReportContext && !hasReportContext) {
        navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE), {
          replace: true,
        });
      }
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    selectedReportTemplate,
    reportBuilderDeliverables.length,
    pendingPromotedTemplateId,
  ]);

  return (
    <DashboardPage title={[PageTitles.REPORT_BUILDER]} loading={false}>
      <Box rounded="lg" h="calc(100vh - 100px)">
        <Stack
          className={styles.panels}
          h="100%"
          direction="row"
          divider={<StackDivider width="15px" className={styles.divider} />}
          spacing="0"
        >
          <Flex
            className={styles.stepsPanel}
            width="240px"
            height="100%"
            padding="32px 24px 16px"
            flexDirection="column"
            justifyContent="space-between"
            rounded="lg"
          >
            {isThemedAppearance ? (
              <ThemedStepper currentStepIndex={currentStepIndex} />
            ) : (
              <LegacyStepper currentStepIndex={currentStepIndex} />
            )}
          </Flex>
          <ReportBuilderPageOutlet />
        </Stack>
      </Box>
    </DashboardPage>
  );
};

const ReportBuilderPageOutlet = () => {
  const { selectedReportTemplate } = useContext(CustomReportBuilderContext);
  const launchSeq = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.launchSeq
  );
  const pageConfig =
    selectedReportTemplate ?? REPORT_TEMPLATES['report-company-overview'];
  const firstTab = pageConfig.tabs?.[0];

  return (
    <FilterProvider
      key={`${pageConfig.id}:${launchSeq}`}
      pageId={pageConfig.id}
      revelioPageId={pageConfig.revelioPageId}
      primaryFilterId={firstTab?.tabContent?.primaryFilter?.id}
      primaryFilterSelectionLimit={
        firstTab?.tabContent?.primaryFilter?.selectionLimit
      }
      primaryFilterSelectionDefault={
        firstTab?.tabContent?.primaryFilter?.selectionDefault
      }
      defaultSelectionOverrides={{
        [FilterId.COMPANY]: [],
        [FilterId.GEOGRAPHY]: [],
        [FilterId.ROLE]: [],
      }}
      persistScope={false}
    >
      <PageStateProvider
        initialPageState={{
          metricOverrides: {},
          selections: {},
          primaryEntities: {},
          multiPrimaryEntities: {},
          dimensionSourceOverrides: {},
          chartSwitcherPlotId: null,
          initialSelections: {},
          activeTabId: firstTab?.id ?? 'tab-1',
        }}
      >
        <PageProvider initialConfig={pageConfig}>
          {() => <Outlet />}
        </PageProvider>
      </PageStateProvider>
    </FilterProvider>
  );
};
